import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { currencySymbol, groupStoresByCity, type CityGroup, type Store } from '@sp/shared'
import { searchKey, storeSub } from '../lib/format.ts'
import { liftRow, restingRect, useFlip, type Lift } from '../lib/springDrag.ts'
import { Icon } from './Icon.tsx'
import { Button, Checkbox, IconButton, Paragraph, TextCol, TextField } from './ui.tsx'

/** Od tolika obchodu jsou mesta sbalena a nabizi se hledani. */
const COLLAPSE_FROM = 8

const COUNTRY_NAMES = { DE: 'Německo', PL: 'Polsko' } as const

function moved(order: readonly string[], id: string, index: number): string[] {
  const next = order.filter((x) => x !== id)
  next.splice(Math.max(0, Math.min(index, next.length)), 0, id)
  return next
}

/** „Zittau · Německo", „Bez města". */
function cityLabel(group: CityGroup): string {
  const city = group.city ?? 'Bez města'
  return group.country ? `${city} · ${COUNTRY_NAMES[group.country]}` : city
}

function formatNote(store: Store): string | undefined {
  if (store.offerFormat === 'hypermarket') return 'Hypermarket'
  if (store.offerFormat === 'supermarket') return 'Supermarket'
  return undefined
}

/**
 * Trasa konkretniho nakupu: vybrane obchody v poradi, jak se projizdi
 * (tazenim nebo sipkami), pod nimi obchody skupiny podle mesta k zaskrtnuti.
 * Vychozi poradi pro nove nakupy je v nastaveni skupiny.
 */
export function RouteEditor({
  stores,
  selected,
  onChange,
}: {
  /** Vsechny obchody skupiny; skryte se nabizeji jen, kdyz uz na trase jsou. */
  stores: readonly Store[]
  selected: readonly string[]
  onChange(next: string[]): void
}) {
  const listRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const stopDrag = useRef<(() => void) | null>(null)
  /** Drzena kopie radku (Pruzina); null s omezenym pohybem v systemu. */
  const lift = useRef<Lift | null>(null)
  const captureRows = useFlip(listRef, '[data-route-id]', 'data-route-id')
  // Posluchace na window ctou aktualni poradi odsud, ne z uzaveru pri zacatku tahu.
  const latest = useRef({ selected, onChange })
  latest.current = { selected, onChange }

  useEffect(
    () => () => {
      stopDrag.current?.()
      lift.current?.destroy()
    },
    [],
  )

  const chosen = selected.flatMap((id) => stores.find((s) => s.id === id) ?? [])
  const pool = stores.filter((s) => !s.archived || selected.includes(s.id))
  // S hodne obchody (Cesko, Nemecko, Polsko) jsou mesta sbalena, jinak by se v seznamu hledalo.
  const many = pool.length >= COLLAPSE_FROM
  const q = searchKey(query)
  const groups = groupStoresByCity(pool)
    .map((group) => ({
      ...group,
      key: group.city === null ? '' : searchKey(group.city),
      label: cityLabel(group),
      stores: q ? group.stores.filter((s) => searchKey(`${s.name} ${s.city ?? ''}`).includes(q)) : group.stores,
    }))
    .filter((group) => group.stores.length > 0)

  function toggle(id: string) {
    captureRows()
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])
  }

  function selectAll(group: readonly Store[]) {
    captureRows()
    onChange([...selected, ...group.filter((s) => !selected.includes(s.id)).map((s) => s.id)])
  }

  function toggleCity(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function startDrag(e: PointerEvent<HTMLButtonElement>, id: string) {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    e.preventDefault()
    stopDrag.current?.()
    const pointerId = e.pointerId
    lift.current?.destroy()
    const rowEl = e.currentTarget.closest<HTMLElement>('[data-route-id]')
    lift.current = rowEl ? liftRow(rowEl, e.clientX, e.clientY) : null
    setDragging(id)
    document.body.classList.add('is-dragging')

    const onMove = (ev: globalThis.PointerEvent) => {
      if (ev.pointerId !== pointerId || !listRef.current) return
      lift.current?.move(ev.clientX, ev.clientY)
      const rows = [...listRef.current.querySelectorAll<HTMLElement>('[data-route-id]')].filter(
        (row) => row.dataset['routeId'] !== id,
      )
      const index = rows.filter((row) => {
        const rect = restingRect(row)
        return rect.top + rect.height / 2 < ev.clientY
      }).length
      const { selected: current, onChange: change } = latest.current
      if (current.indexOf(id) !== index) {
        captureRows()
        change(moved(current, id, index))
      }
    }
    const onEnd = (ev: globalThis.PointerEvent) => {
      if (ev.pointerId !== pointerId) return
      cleanup()
      const landed = lift.current
      lift.current = null
      landed?.drop(() => listRef.current?.querySelector<HTMLElement>(`[data-route-id="${CSS.escape(id)}"]`) ?? null)
    }
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
      document.body.classList.remove('is-dragging')
      stopDrag.current = null
      setDragging(null)
    }
    stopDrag.current = cleanup
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
  }

  function onGripKey(e: KeyboardEvent<HTMLButtonElement>, id: string) {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    captureRows()
    const index = selected.indexOf(id)
    onChange(moved(selected, id, e.key === 'ArrowUp' ? index - 1 : index + 1))
  }

  return (
    <div className="stack-8">
      <div ref={listRef} className="stack-8" hidden={chosen.length === 0}>
        {chosen.map((store, index) => (
          <div key={store.id} data-route-id={store.id} className={`route-row${dragging === store.id ? ' is-dragging' : ''}`}>
            <button
              type="button"
              className="grip"
              aria-label={`Přesunout ${store.name}, ${index + 1}. na trase. Táhni, nebo použij šipky nahoru a dolů.`}
              onPointerDown={(e) => startDrag(e, store.id)}
              onKeyDown={(e) => onGripKey(e, store.id)}
            >
              <Icon name="grip" size={20} />
            </button>
            <span className="num-badge">{index + 1}</span>
            <TextCol title={store.name} sub={storeSub(store)} />
            <IconButton
              icon="x"
              tone="muted"
              label={`Odebrat ${store.name} z trasy`}
              onClick={() => {
                captureRows()
                onChange(selected.filter((x) => x !== store.id))
              }}
            />
          </div>
        ))}
      </div>
      {pool.length > 0 && (
        <div className="stack-8">
          {chosen.length > 0 && <span className="field-label route-others-label">Obchody skupiny podle města</span>}
          {many && (
            <TextField
              type="search"
              aria-label="Hledat obchod nebo město"
              placeholder="Hledat obchod nebo město"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          {groups.map((group) => {
            const picked = group.stores.filter((s) => selected.includes(s.id)).length
            const open = !many || q !== '' || expanded.has(group.key)
            return (
              <div key={group.key} className="city-group">
                {many && q === '' ? (
                  <button
                    type="button"
                    className="city-toggle"
                    aria-expanded={open}
                    onClick={() => toggleCity(group.key)}
                  >
                    <Icon name="store" size={18} className="tone-muted" />
                    <TextCol
                      title={group.label}
                      sub={open ? undefined : group.stores.map((s) => s.name).join(' · ')}
                    />
                    <span className={`currency-pill${picked > 0 ? ' is-picked' : ''}`}>
                      {picked}/{group.stores.length}
                    </span>
                    <Icon name={open ? 'chevron_up' : 'chevron_down'} size={18} className="tone-muted" />
                  </button>
                ) : (
                  <span className="city-name">{group.label}</span>
                )}
                {open && (
                  <div className="store-picks">
                    {group.stores.map((store) => {
                      const on = selected.includes(store.id)
                      return (
                        <button
                          key={store.id}
                          type="button"
                          role="checkbox"
                          aria-checked={on}
                          className="store-pick"
                          onClick={() => toggle(store.id)}
                        >
                          <Checkbox checked={on} />
                          <TextCol title={store.name} sub={formatNote(store)} />
                          <span className="currency-pill">{currencySymbol(store.currency)}</span>
                        </button>
                      )
                    })}
                    {group.stores.length > 1 && picked < group.stores.length && (
                      <Button kind="text" compact className="self-start" onClick={() => selectAll(group.stores)}>
                        Vybrat vše
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {q !== '' && groups.length === 0 && <Paragraph size="meta">Žádný obchod ani město neodpovídá.</Paragraph>}
        </div>
      )}
    </div>
  )
}
